/**
 * Pilot surface guard — pure route/menu fold logic for P1b.
 *
 * @see src/shared/platform/pilot-surface-registry.ts
 * @see src/shared/types/tenant-pilot-settings.ts
 */

import {
  listPilotSurfaceRouteHints,
  resolvePilotPresetSurfaceMap,
  type PilotSurfaceId,
} from '@shared/platform/pilot-surface-registry';
import {
  DEFAULT_TENANT_PILOT_PRESET_ID,
  type TenantPilotPresetId,
  type TenantPilotSettings,
} from '@shared/types/tenant-pilot-settings';

export const PILOT_FOLD_FALLBACK_PATH = '/workspace';

export const PILOT_FOLD_BLOCKED_TITLE = '파일럿 모드';

export const PILOT_FOLD_BLOCKED_MESSAGE =
  '이 화면은 파일럿 모드에서 접혀 있습니다. 주문·업무 처리 루프에 필요한 메뉴만 표시됩니다.';

export interface PilotSurfaceGuardState {
  pilotModeActive: boolean;
  visibleRoutes: readonly string[];
  hiddenRoutes: readonly string[];
}

export function normalizePilotPath(path: string): string {
  if (!path) return '/';
  const trimmed = path.trim();
  if (trimmed.length > 1 && trimmed.endsWith('/')) {
    return trimmed.slice(0, -1);
  }
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

export function pathMatchesPilotRouteHint(pathname: string, hint: string): boolean {
  const path = normalizePilotPath(pathname);
  const route = normalizePilotPath(hint);
  return path === route || path.startsWith(`${route}/`);
}

export function pathMatchesAnyPilotRouteHint(pathname: string, hints: readonly string[]): boolean {
  return hints.some((hint) => pathMatchesPilotRouteHint(pathname, hint));
}

export function resolvePilotSurfaceGuardState(
  pilotSettings?: TenantPilotSettings | null
): PilotSurfaceGuardState {
  if (!pilotSettings?.pilotMode) {
    return { pilotModeActive: false, visibleRoutes: [], hiddenRoutes: [] };
  }

  const presetId: TenantPilotPresetId =
    pilotSettings.pilotPresetId ?? DEFAULT_TENANT_PILOT_PRESET_ID;
  const preset = resolvePilotPresetSurfaceMap(presetId);

  const enabledSurfaces = (pilotSettings.enabledSurfaces ??
    preset?.enabledSurfaces ??
    []) as readonly PilotSurfaceId[];
  const hiddenSurfaces = (pilotSettings.hiddenSurfaces ??
    preset?.hiddenSurfaces ??
    []) as readonly PilotSurfaceId[];

  return {
    pilotModeActive: true,
    visibleRoutes: listPilotSurfaceRouteHints(enabledSurfaces),
    hiddenRoutes: listPilotSurfaceRouteHints(hiddenSurfaces),
  };
}

function longestMatchingPilotHintLength(pathname: string, hints: readonly string[]): number {
  let best = 0;
  for (const hint of hints) {
    if (pathMatchesPilotRouteHint(pathname, hint)) {
      best = Math.max(best, normalizePilotPath(hint).length);
    }
  }
  return best;
}

/** Visible route hints win at equal specificity; longer hidden hints beat shorter visible prefixes. */
export function isPathHiddenByPilotFold(
  pathname: string,
  guard: Pick<PilotSurfaceGuardState, 'pilotModeActive' | 'visibleRoutes' | 'hiddenRoutes'>
): boolean {
  if (!guard.pilotModeActive) {
    return false;
  }

  const visibleLength = longestMatchingPilotHintLength(pathname, guard.visibleRoutes);
  const hiddenLength = longestMatchingPilotHintLength(pathname, guard.hiddenRoutes);

  if (hiddenLength === 0) {
    return false;
  }
  if (visibleLength === 0) {
    return true;
  }
  return hiddenLength > visibleLength;
}

export function resolvePilotFoldFallbackPath(
  guard: Pick<PilotSurfaceGuardState, 'visibleRoutes'>
): string {
  const preferred = ['/workspace', '/workspace/assignments', '/system/dashboard'];
  for (const candidate of preferred) {
    if (pathMatchesAnyPilotRouteHint(candidate, guard.visibleRoutes)) {
      return candidate;
    }
  }
  return guard.visibleRoutes[0] ?? PILOT_FOLD_FALLBACK_PATH;
}
